// ============================================
// 2️⃣ COMPONENTE DE CONFIGURAÇÃO (criar em: src/components/TimeConfigControl.js)
// ============================================

import React, { useState, useEffect } from 'react';
import { timeManager } from '../../utils/timeManager';

function TimeConfigControl({ isOpen, onClose, clienteId }) {
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(timeManager.getConfig());
  const [manualHours,setManualHours]=useState('');
  const [manualMinutes,setManualMinutes]=useState('0');
  const [currentTime, setCurrentTime] = useState(timeManager.getFormattedTime());
  const [systemTime, setSystemTime] = useState(new Date().toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  }));

  // Sincronizar com o backend na montagem
  useEffect(() => {
    let active=true;
    setConfig(timeManager.getConfig());
    timeManager.syncFromBackend().then(ok => {
      if(!active)return;
      const saved=timeManager.getConfig();setConfig(saved);
      setError(!ok?'Não foi possível confirmar o horário salvo da loja.':saved.configuration?.schemaVersion==='store-time-config/legacy'
        ?'Confirme o fuso desta loja para habilitar a locução com horário.':'');
    });
    return ()=>{active=false;};
  }, [clienteId]);

  const persist=async value=>{
    if(saving)return;
    setSaving(true);setError('');
    try{const ok=await timeManager.saveConfig(value);setConfig(timeManager.getConfig());if(!ok)setError('Horário não confirmado. Recarregue a configuração e tente novamente.');}
    finally{setSaving(false);}
  };

  // Atualizar relógios a cada segundo
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(timeManager.getFormattedTime());
      setSystemTime(new Date().toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      }));
    }, 1000);

    return () => clearInterval(interval);
  }, [config]);

  const handlePresetChange = async (preset) => {
    const newConfig = {
      offsetMinutes: preset.offset,
      timezone:      preset.name,
      customName:    null,
    };

    await persist(newConfig);
  };

  const handleCustomOffset = async (hours, minutes) => {
    if(!Number.isInteger(hours)||!Number.isInteger(minutes)||Math.abs(hours)>14||minutes<0||minutes>59
      ||(Math.abs(hours)===14&&minutes!==0)){setError('Informe horas de -14 a 14 e minutos de 0 a 59.');return;}
    const totalMinutes = (hours * 60) + (hours<0?-minutes:minutes);

    const newConfig = {
      offsetMinutes: totalMinutes,
      timezone:      'Customizado',
      customName:    `UTC${totalMinutes >= 0 ? '+' : ''}${(totalMinutes / 60).toFixed(1).replace('.0', '')}`,
    };

    await persist(newConfig);
  };

  const handleReset = async () => {
    await persist({offsetMinutes:-new Date().getTimezoneOffset(),timezone:'Sistema'});
  };

  const presets = timeManager.getTimezonePresets();
  const systemUtcOffset = -new Date().getTimezoneOffset();
  const diffFromSystemMinutes = (Number(config.offsetMinutes) || 0) - systemUtcOffset;
  const hasLocalAdjustment = diffFromSystemMinutes !== 0;

  if (!isOpen) return null;

  return (
    <>
      {/* overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.4)',
          zIndex: 1000
        }}
      />
      {/* painel */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1001,
        background: 'var(--bg-surface)',
        borderRadius: '16px',
        padding: '20px',
        width: '350px',
        maxHeight: 'calc(100vh - 80px)',
        overflowY: 'auto',
        border: '1px solid var(--border)',
        color: 'var(--text-primary)',
        animation: 'slideIn 0.2s ease-out'
      }}>
        <style>
          {`
            @keyframes slideIn {
              from { opacity: 0; transform: translate(-50%, -52%); }
              to   { opacity: 1; transform: translate(-50%, -50%); }
            }
          `}
        </style>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: 15 }}>Relógio do Sistema</h3>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(var(--error-rgb), 0.15)',
              border: '1px solid rgba(var(--error-rgb), 0.3)',
              color: 'var(--error)',
              borderRadius: '6px',
              width: '30px',
              height: '30px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            ✕
          </button>
        </div>

        {error && <p role="alert">{error}</p>}
        {saving && <p role="status">Confirmando horário da loja…</p>}
        {/* Display de horas */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '10px',
          marginBottom: '20px'
        }}>
          <div style={{
            padding: '15px',
            background: 'rgba(var(--brand-rgb), 0.2)',
            borderRadius: '10px',
            border: '1px solid rgba(var(--brand-rgb), 0.3)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '11px', opacity: 0.7, marginBottom: '5px' }}>
              Sistema
            </div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--brand-400)' }}>
              {systemTime}
            </div>
          </div>

          <div style={{
            padding: '15px',
            background: hasLocalAdjustment
              ? 'rgba(var(--warning-rgb), 0.2)'
              : 'rgba(var(--success-rgb), 0.2)',
            borderRadius: '10px',
            border: hasLocalAdjustment
              ? '1px solid rgba(var(--warning-rgb), 0.3)'
              : '1px solid rgba(var(--success-rgb), 0.3)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '11px', opacity: 0.7, marginBottom: '5px' }}>
              Rádio
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: hasLocalAdjustment ? 'var(--warning)' : 'var(--success)'
            }}>
              {currentTime}
            </div>
          </div>
        </div>

        {/* Info do offset */}
        {hasLocalAdjustment && (
          <div style={{
            padding: '10px',
            background: 'rgba(var(--warning-rgb), 0.2)',
            border: '1px solid rgba(var(--warning-rgb), 0.3)',
            borderRadius: '8px',
            marginBottom: '15px',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>⚠️</span>
            <div>
              <strong>Diferença ativa:</strong> {Math.abs(diffFromSystemMinutes)} minutos
              {diffFromSystemMinutes > 0 ? ' adiantado' : ' atrasado'}
            </div>
          </div>
        )}

        {/* Presets de fuso horário */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>
            Fusos Horários
          </label>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            {presets.map(preset => (
              <button
                key={preset.name}
                disabled={saving}
                onClick={() => handlePresetChange(preset)}
                style={{
                  padding: '12px',
                  background: config.timezone === preset.name
                    ? 'rgba(var(--brand-rgb), 0.3)'
                    : 'rgba(var(--brand-rgb), 0.1)',
                  border: config.timezone === preset.name
                    ? '2px solid var(--brand-500)'
                    : '1px solid rgba(var(--brand-rgb), 0.3)',
                  borderRadius: '8px',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '13px',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ fontWeight: 'bold' }}>{preset.name}</div>
                <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '2px' }}>
                  {preset.description}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Ajuste customizado */}
        <div style={{
          padding: '15px',
          background: 'var(--bg-deep)',
          borderRadius: '10px',
          marginBottom: '15px'
        }}>
          <label style={{ fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>
            Ajuste Manual
          </label>
          <div style={{ fontSize: '11px', opacity: 0.7, marginBottom: '10px' }}>
            Informe o fuso UTC real da loja. Ex.: Brasília -3, Mato Grosso -4.
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              type="number"
              placeholder="Horas"
              min="-14"
              max="14"
              value={manualHours}
              disabled={saving}
              style={{
                flex: 1,
                padding: '8px',
                borderRadius: '6px',
                border: '1px solid rgba(var(--brand-rgb), 0.3)',
                background: 'rgba(var(--brand-rgb), 0.1)',
                color: 'var(--text-primary)',
                fontSize: '13px'
              }}
              onChange={(e) => {
                setManualHours(e.target.value);
              }}
            />
            <span>:</span>
            <input
              type="number"
              placeholder="Min"
              min="0"
              max="59"
              step="15"
              value={manualMinutes}
              disabled={saving}
              style={{
                flex: 1,
                padding: '8px',
                borderRadius: '6px',
                border: '1px solid rgba(var(--brand-rgb), 0.3)',
                background: 'rgba(var(--brand-rgb), 0.1)',
                color: 'var(--text-primary)',
                fontSize: '13px'
              }}
              onChange={(e) => {
                setManualMinutes(e.target.value);
              }}
            />
          </div>
          <button disabled={saving||manualHours.trim()===''||manualMinutes.trim()===''}
            onClick={()=>handleCustomOffset(Number(manualHours),Number(manualMinutes))}>Aplicar ajuste manual</button>
        </div>

        {/* Botão resetar */}
        <button
          onClick={handleReset}
          disabled={saving||(!hasLocalAdjustment && config.timezone === 'Sistema')}
          style={{
            width: '100%',
            padding: '12px',
            background: (!hasLocalAdjustment && config.timezone === 'Sistema')
              ? 'var(--bg-deep)'
              : 'var(--error)',
            border: 'none',
            borderRadius: '8px',
            color: 'var(--text-primary)',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: (!hasLocalAdjustment && config.timezone === 'Sistema') ? 'not-allowed' : 'pointer',
            opacity: (!hasLocalAdjustment && config.timezone === 'Sistema') ? 0.5 : 1,
            transition: 'all 0.2s'
          }}
        >
          🔄 Resetar para Hora do Sistema
        </button>

        {/* Info */}
        <div style={{
          marginTop: '15px',
          padding: '10px',
          background: 'rgba(var(--brand-rgb), 0.1)',
          border: '1px solid rgba(var(--brand-rgb), 0.2)',
          borderRadius: '8px',
          fontSize: '11px',
          opacity: 0.8
        }}>
          <strong>💡 Dica:</strong> O locutor automático usará o horário configurado aqui.
          Útil para operar em fusos diferentes do computador.
        </div>
      </div>
    </>
  );
}

export default TimeConfigControl;
